import React, { useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { getErrorDetail } from "@/lib/api";
import { ArrowLeft, KeyRound, GraduationCap } from "lucide-react";

const ADMIN_HINT_EMAIL = "dawnvilleas2026@gmail.com";

export default function LoginPage() {
  const { t } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const isAdminMode = params.get("role") === "admin";
  const [email, setEmail] = useState(isAdminMode ? ADMIN_HINT_EMAIL : "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault(); setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success(`Welcome, ${u.name || u.email}`);
      const next = loc.state?.from || (u.role === "admin" ? "/admin" : "/portal");
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(getErrorDetail(err, t.auth.bad_creds));
    } finally { setLoading(false); }
  }

  return (
    <main data-testid="login-page" className="min-h-[calc(100vh-4rem)] grid grid-cols-1 md:grid-cols-12">
      <div className="md:col-span-7 relative hidden md:block">
        <img src="https://images.unsplash.com/photo-1532012197267-da84d127e765?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[hsl(var(--background))]/95 via-[hsl(var(--background))]/20 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="inline-flex items-center gap-1 text-xs tracking-[0.25em] uppercase text-[hsl(var(--foreground))]/70 hover:text-[hsl(var(--primary))]"><ArrowLeft className="w-3 h-3" />{t.common.back_home}</Link>
          <p className="font-display text-5xl tracking-tight font-medium max-w-md leading-tight">A studio that remembers its students.</p>
        </div>
      </div>

      <div className="md:col-span-5 flex items-center justify-center p-8 md:p-16 bg-[hsl(var(--background))]">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <p className="text-xs tracking-[0.25em] text-[hsl(var(--muted-foreground))] flex items-center gap-2">
              {isAdminMode ? <KeyRound className="w-3 h-3" /> : <GraduationCap className="w-3 h-3" />}
              {isAdminMode ? "DAWNVILLE — ADMIN ACCESS" : "DAWNVILLE — STUDENT PORTAL"}
            </p>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight font-medium mt-3">{isAdminMode ? t.auth.admin_login_title : t.auth.login_title}</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">{isAdminMode ? t.auth.admin_login_sub : t.auth.login_sub}</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{t.auth.email}</Label>
              <Input id="email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="login-email" className="mt-2" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{t.auth.password}</Label>
              <Input id="password" type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} data-testid="login-password" className="mt-2" />
            </div>
          </div>
          <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-[hsl(var(--foreground))] hover:bg-[hsl(var(--foreground))]/90 text-[hsl(var(--background))] rounded-full">{loading ? "…" : t.auth.submit_login}</Button>
          <div className="flex justify-between items-center text-sm text-[hsl(var(--muted-foreground))]">
            <Link to="/forgot-password" data-testid="login-forgot-link" className="hover:text-[hsl(var(--primary))]">{t.auth.forgot_link}</Link>
            <Link to="/register" data-testid="login-to-register" className="hover:text-[hsl(var(--primary))]">{t.auth.switch_to_register}</Link>
          </div>
        </form>
      </div>
    </main>
  );
}
